import React, { useState, useEffect, useRef } from "react";
import PropTypes from "prop-types";
import { useNavigate, useLocation } from "react-router-dom";
import { useDispatch } from "react-redux";
import { userLogout } from "../../redux/loginSlice";
import {
  PARTNER_DASHBOARD,
  PARTNER_CLIENTS,
  PARTNER_COMMISSION,
  INVOICE,
  ACCOUNT
} from "../../route/paths";
import { FT } from "./partnerPortalShared";
import { hasRole, ROLE_CLIENT } from "../../utils/accountRoles";
import fundtapLogo from "../../assets/images/logo.png";
import LoginAsUser from "../../components/loginasuser/LoginAsUser";

// Partner-portal header (nav redesign v2). Four elements only:
// logo + destinations, the Partner/My-FundTap workspace toggle (dual accounts),
// and an avatar menu (settings + log out). The referral link and "Add client"
// action live on the page content, not here.
export default function PartnerPortalHeader({ partnerName, isLead }) {
  const navigate = useNavigate();
  const location = useLocation();
  const dispatch = useDispatch();
  const [menuOpen, setMenuOpen] = useState(false);
  const menuRef = useRef(null);

  // Dual accounts (partner + FundTap client) get the workspace toggle.
  const alsoClient = hasRole(ROLE_CLIENT);

  // Close the avatar menu on outside click / Escape.
  useEffect(() => {
    if (!menuOpen) return undefined;
    const onClick = (e) => {
      if (menuRef.current && !menuRef.current.contains(e.target))
        setMenuOpen(false);
    };
    const onKey = (e) => {
      if (e.key === "Escape") setMenuOpen(false);
    };
    document.addEventListener("mousedown", onClick);
    document.addEventListener("keydown", onKey);
    return () => {
      document.removeEventListener("mousedown", onClick);
      document.removeEventListener("keydown", onKey);
    };
  }, [menuOpen]);

  const initials = (partnerName || "?")
    .split(/\s+/)
    .map((s) => s.charAt(0).toUpperCase())
    .slice(0, 2)
    .join("");

  const navItems = [
    { id: "dashboard", label: "Dashboard", path: PARTNER_DASHBOARD },
    {
      id: "clients",
      label: isLead ? "Partners" : "Clients",
      path: PARTNER_CLIENTS
    },
    { id: "commission", label: "Commission", path: PARTNER_COMMISSION }
  ];
  const isActive = (item) => {
    if (item.id === "commission")
      return location.pathname.startsWith(PARTNER_COMMISSION);
    if (item.id === "clients")
      return location.pathname.startsWith(PARTNER_CLIENTS);
    return location.pathname.startsWith(PARTNER_DASHBOARD);
  };

  return (
    <>
      {/* Staff impersonation banner — renders only when acting as this partner,
          giving a "return to admin" link inside the portal. */}
      <LoginAsUser />
      <div style={{ height: 4, background: FT.navy }} />
      <header
        style={{
          background: "#fff",
          borderBottom: `1px solid ${FT.navy08}`,
          height: 66,
          padding: "0 48px",
          display: "flex",
          alignItems: "center",
          justifyContent: "space-between",
          fontFamily: FT.font
        }}
      >
        {/* Left: logo + destinations */}
        <div
          style={{
            display: "flex",
            alignItems: "stretch",
            gap: 40,
            height: "100%"
          }}
        >
          <div style={{ display: "flex", alignItems: "center" }}>
            <img
              src={fundtapLogo}
              alt="FundTap"
              onClick={() => navigate(PARTNER_DASHBOARD)}
              style={{ height: 26, cursor: "pointer", display: "block" }}
            />
          </div>
          <nav
            style={{ display: "flex", alignItems: "stretch", height: "100%" }}
          >
            {navItems.map((n) => {
              const active = isActive(n);
              return (
                <button
                  key={n.id}
                  onClick={() => navigate(n.path)}
                  style={{
                    border: "none",
                    background: "transparent",
                    cursor: "pointer",
                    padding: "0 14px",
                    fontFamily: "inherit",
                    fontSize: 14.5,
                    fontWeight: 600,
                    color: active ? FT.navy : FT.navy60,
                    borderBottom: `2px solid ${active ? FT.mint : "transparent"}`,
                    display: "inline-flex",
                    alignItems: "center"
                  }}
                >
                  {n.label}
                </button>
              );
            })}
          </nav>
        </div>

        {/* Right: workspace toggle + avatar menu */}
        <div style={{ display: "flex", alignItems: "center", gap: 14 }}>
          {alsoClient && (
            <div
              style={{
                display: "inline-flex",
                alignItems: "center",
                background: FT.navy04,
                border: `1px solid ${FT.navy15}`,
                borderRadius: 999,
                padding: 3,
                gap: 2
              }}
            >
              <button
                type="button"
                style={{
                  padding: "6px 14px",
                  borderRadius: 999,
                  fontSize: 12.5,
                  fontWeight: 700,
                  border: "none",
                  cursor: "default",
                  fontFamily: "inherit",
                  background: FT.navy,
                  color: "#fff",
                  boxShadow: "0 1px 3px rgba(0,33,61,0.25)"
                }}
              >
                Partner
              </button>
              <button
                type="button"
                onClick={() => navigate(INVOICE)}
                title="Switch to your FundTap customer account"
                style={{
                  padding: "6px 14px",
                  borderRadius: 999,
                  fontSize: 12.5,
                  fontWeight: 700,
                  border: "none",
                  cursor: "pointer",
                  fontFamily: "inherit",
                  background: "transparent",
                  color: FT.navy60
                }}
              >
                My FundTap
              </button>
            </div>
          )}

          <div style={{ position: "relative" }} ref={menuRef}>
            <button
              onClick={() => setMenuOpen((o) => !o)}
              style={{
                display: "inline-flex",
                alignItems: "center",
                gap: 6,
                padding: "4px 10px 4px 4px",
                background: "#fff",
                border: `1px solid ${FT.navy15}`,
                borderRadius: 999,
                cursor: "pointer",
                fontFamily: "inherit"
              }}
            >
              <span
                style={{
                  width: 32,
                  height: 32,
                  borderRadius: 999,
                  background: FT.mintBg,
                  color: FT.navy,
                  display: "grid",
                  placeItems: "center",
                  fontWeight: 800,
                  fontSize: 12
                }}
              >
                {initials}
              </span>
              <span style={{ color: FT.navy40, fontSize: 11 }}>▾</span>
            </button>

            {menuOpen && (
              <div
                style={{
                  position: "absolute",
                  right: 0,
                  top: "calc(100% + 8px)",
                  width: 300,
                  background: "#fff",
                  border: `1px solid ${FT.navy08}`,
                  borderRadius: 12,
                  boxShadow: "0 8px 30px rgba(0,33,61,0.14)",
                  overflow: "hidden",
                  zIndex: 50
                }}
              >
                <div
                  style={{
                    padding: "14px 18px 12px",
                    borderBottom: `1px solid ${FT.navy08}`
                  }}
                >
                  <div
                    style={{ fontSize: 14.5, fontWeight: 700, color: FT.navy }}
                  >
                    {partnerName || "Partner"}
                  </div>
                  <div
                    style={{ fontSize: 12.5, color: FT.navy60, marginTop: 2 }}
                  >
                    Referral partner
                  </div>
                </div>
                <button
                  onClick={() => {
                    setMenuOpen(false);
                    navigate(ACCOUNT);
                  }}
                  style={menuItemStyle}
                >
                  <svg
                    width="15"
                    height="15"
                    viewBox="0 0 24 24"
                    fill="none"
                    stroke="currentColor"
                    strokeWidth="2"
                    strokeLinecap="round"
                    strokeLinejoin="round"
                  >
                    <circle cx="12" cy="12" r="3" />
                    <path d="M19.4 15a1.65 1.65 0 0 0 .33 1.82l.06.06a2 2 0 1 1-2.83 2.83l-.06-.06a1.65 1.65 0 0 0-1.82-.33 1.65 1.65 0 0 0-1 1.51V21a2 2 0 0 1-4 0v-.09A1.65 1.65 0 0 0 9 19.4a1.65 1.65 0 0 0-1.82.33l-.06.06a2 2 0 1 1-2.83-2.83l.06-.06a1.65 1.65 0 0 0 .33-1.82 1.65 1.65 0 0 0-1.51-1H3a2 2 0 0 1 0-4h.09A1.65 1.65 0 0 0 4.6 9a1.65 1.65 0 0 0-.33-1.82l-.06-.06a2 2 0 1 1 2.83-2.83l.06.06a1.65 1.65 0 0 0 1.82.33H9a1.65 1.65 0 0 0 1-1.51V3a2 2 0 0 1 4 0v.09a1.65 1.65 0 0 0 1 1.51 1.65 1.65 0 0 0 1.82-.33l.06-.06a2 2 0 1 1 2.83 2.83l-.06.06a1.65 1.65 0 0 0-.33 1.82V9a1.65 1.65 0 0 0 1.51 1H21a2 2 0 0 1 0 4h-.09a1.65 1.65 0 0 0-1.51 1z" />
                  </svg>
                  Settings &amp; payout details
                </button>
                <div
                  style={{ height: 1, background: FT.navy08, margin: "4px 0" }}
                />
                <button
                  onClick={() => dispatch(userLogout())}
                  style={{ ...menuItemStyle, color: "#B4443C" }}
                >
                  <svg
                    width="15"
                    height="15"
                    viewBox="0 0 24 24"
                    fill="none"
                    stroke="currentColor"
                    strokeWidth="2"
                    strokeLinecap="round"
                    strokeLinejoin="round"
                  >
                    <path d="M9 21H5a2 2 0 0 1-2-2V5a2 2 0 0 1 2-2h4" />
                    <polyline points="16 17 21 12 16 7" />
                    <line x1="21" y1="12" x2="9" y2="12" />
                  </svg>
                  Log out
                </button>
              </div>
            )}
          </div>
        </div>
      </header>
    </>
  );
}

PartnerPortalHeader.propTypes = {
  partnerName: PropTypes.string,
  isLead: PropTypes.bool
};

const menuItemStyle = {
  display: "flex",
  alignItems: "center",
  gap: 10,
  width: "100%",
  padding: "10px 18px",
  background: "transparent",
  border: "none",
  cursor: "pointer",
  fontFamily: "inherit",
  fontSize: 13.5,
  fontWeight: 600,
  color: "#475569",
  textAlign: "left"
};
